// The shared A4 "report sheet": the paper every clinical report PDF is printed
// on, and the html2pdf → jsPDF plumbing that turns markup into it.
//
// A caller supplies only its own body markup; this module owns the page box,
// the typography, the identification table, page breaking, and the letterhead
// stamp. Radiology (pages/radiology-technician/utils/printRadiologyReport.js)
// and lab (pages/lab-technician/utils/printLabReport.js) both go through here,
// which is what keeps their output identical to each other and to the discharge
// summary.
import html2pdf from "html2pdf.js";
import { stampLetterhead } from "./reportLetterhead";

export const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// 29-Jul-2026. Unambiguous on a clinical document in a way a locale-formatted
// "7/29/2026" is not.
export const formatReportDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const day = String(d.getDate()).padStart(2, "0");
  return `${day}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
};

export const formatAgeGender = (age, gender) => {
  const a = age === 0 || age ? `${age} Y` : "";
  const g = gender && gender !== "-" ? String(gender).trim() : "";
  if (a && g) return `${a} / ${g}`;
  return a || g || "-";
};

// Every rule is scoped under .rp-doc so injecting this into document.head while
// html2canvas rasterises cannot touch the app behind the modal.
export const SHEET_CSS = `
  .rp-doc { font-family: Arial, Helvetica, sans-serif; color: #111827; background: #ffffff; }
  .rp-doc .rp-title { font-size: 17px; font-weight: 700; text-align: center;
                      letter-spacing: .06em; text-transform: uppercase; margin: 0 0 16px; }

  /* Identification box: one bordered frame split in half by a full-height rule,
     label : value rows down each side. Built from tables rather than grid —
     html2canvas rasterises table borders far more predictably. */
  .rp-doc .rp-idbox { width: 100%; border: 1px solid #111827; border-collapse: collapse;
                      margin-bottom: 18px; }
  .rp-doc .rp-idbox > tbody > tr > td { width: 50%; vertical-align: top; padding: 10px 14px; }
  .rp-doc .rp-idbox > tbody > tr > td + td { border-left: 1px solid #111827; }
  .rp-doc .rp-idcol { width: 100%; border-collapse: collapse; font-size: 12px; }
  .rp-doc .rp-idcol td { border: none; padding: 3px 0; vertical-align: top; }
  .rp-doc .rp-idcol .k { font-weight: 700; white-space: nowrap; width: 40%; }
  .rp-doc .rp-idcol .c { width: 14px; }

  .rp-doc .rp-body { font-size: 13px; line-height: 1.6; }
  .rp-doc .rp-body h1 { font-size: 17px; margin: 16px 0 8px; }
  .rp-doc .rp-body h2 { font-size: 15px; margin: 14px 0 6px; }
  .rp-doc .rp-body h3 { font-size: 13px; margin: 12px 0 6px; text-transform: uppercase; letter-spacing: .04em; }
  .rp-doc .rp-body p { margin: 0 0 8px; }
  .rp-doc .rp-body ul, .rp-doc .rp-body ol { margin: 0 0 8px 20px; padding: 0; }
  .rp-doc .rp-body table { border-collapse: collapse; width: 100%; margin: 10px 0; }
  .rp-doc .rp-body th, .rp-doc .rp-body td { border: 1px solid #d1d5db; padding: 6px 8px; font-size: 12px; text-align: left; }
  .rp-doc .rp-body th { background: #f3f4f6; font-weight: 700; }
  .rp-doc .rp-body hr { border: none; border-top: 1px solid #d1d5db; margin: 14px 0; }

  /* Page-break control. The body itself must stay splittable — see the pagebreak
     note in renderReportSheet — so only the pieces that read as broken when
     split are pinned. */
  .rp-doc .rp-idbox { page-break-inside: avoid; }
  .rp-doc .rp-body tr { page-break-inside: avoid; }
  .rp-doc .rp-body h1, .rp-doc .rp-body h2, .rp-doc .rp-body h3 {
    page-break-inside: avoid; page-break-after: avoid;
  }
`;

// One half of the identification box: label : value rows. The colon lives in
// its own cell so every value lines up down the column regardless of label
// length.
const detailRows = (rows) =>
  rows
    .map(
      ([label, value]) => `
      <tr>
        <td class="k">${esc(label)}</td>
        <td class="c">:</td>
        <td class="v">${esc(value || "-")}</td>
      </tr>`,
    )
    .join("");

/**
 * The bordered two-column identification box that opens every report.
 * @param {Array<[string, any]>} left  label/value pairs for the left half
 * @param {Array<[string, any]>} right label/value pairs for the right half
 */
export const buildIdBox = (left, right) => `
  <table class="rp-idbox">
    <tbody>
      <tr>
        <td><table class="rp-idcol"><tbody>${detailRows(left)}</tbody></table></td>
        <td><table class="rp-idcol"><tbody>${detailRows(right)}</tbody></table></td>
      </tr>
    </tbody>
  </table>`;

/**
 * Render markup to an A4 PDF Blob.
 *
 * @param {object}  args
 * @param {string}  args.html       the sheet's content (title, id box, body)
 * @param {string}  args.filename
 * @param {object}  args.letterhead parts for stampLetterhead, or null to print
 *   the content alone — the "without letterhead" case, for hospitals running
 *   pre-printed stationery through the tray. The page box does NOT change:
 *   the same top and bottom margins stay reserved so the content still lands
 *   inside the pre-printed header and footer.
 * @param {boolean} args.autoPrint  ask the PDF viewer to open its print dialog
 */
export function renderReportSheet({ html, filename, letterhead, autoPrint = false }) {
  // html2canvas resolves computed styles off the live document, so the rules
  // have to be in the page — not inline on the detached container.
  const styleEl = document.createElement("style");
  styleEl.textContent = SHEET_CSS;
  document.head.appendChild(styleEl);

  const container = document.createElement("div");
  container.className = "rp-doc";
  container.innerHTML = html;

  const wrapper = document.createElement("div");
  wrapper.style.position = "absolute";
  wrapper.style.top = "-9999px";
  wrapper.style.left = "-9999px";
  wrapper.style.width = "794px"; // Fixed A4 width at 96 DPI
  wrapper.style.zIndex = "-9999";
  container.style.width = "100%";
  container.style.backgroundColor = "#ffffff";
  wrapper.appendChild(container);
  document.body.appendChild(wrapper);

  const cleanup = () => {
    document.body.removeChild(wrapper);
    document.head.removeChild(styleEl);
  };

  const opt = {
    // 35mm top clears the header rule at y=30mm; 30mm bottom clears the
    // signature lines at y=275mm. Same box the discharge summary reserves.
    margin: [35, 15, 30, 15],
    filename,
    image: { type: "jpeg", quality: 1.0 },
    html2canvas: { scale: 2, useCORS: true, windowWidth: 794 },
    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    // NOT "avoid-all": that stamps page-break-inside:avoid onto every element,
    // including the single <div class="rp-body"> wrapping the whole report. The
    // wrapper then cannot be split, so a report taller than the space left below
    // the identification box is bumped wholesale to the next page and the first
    // one ends in a blank half. "css" honours the targeted rules in SHEET_CSS
    // instead, which protect only what must genuinely stay whole.
    pagebreak: { mode: ["css", "legacy"] },
  };

  return html2pdf()
    .set(opt)
    .from(container)
    .toPdf()
    .get("pdf")
    .then((pdf) => {
      // Page count is only knowable after layout, which is why the letterhead
      // is stamped here rather than being part of the markup.
      if (letterhead) stampLetterhead(pdf, letterhead);
      if (autoPrint) pdf.autoPrint();
    })
    .output("blob")
    .then((blob) => {
      cleanup();
      return blob;
    })
    .catch((err) => {
      cleanup();
      throw err;
    });
}
